import type {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

const ATTRIBUTE_NODE_TYPE = 'Attribute' as ComposerAttributeType;

export const ComposerValidationErrorCode = {
    ATTRIBUTE_NAME_REQUIRED: 'attribute-name-required',
    ATTRIBUTE_NAME_NOT_UNIQUE: 'attribute-name-not-unique',
    ATTRIBUTE_CODE_REQUIRED: 'attribute-code-required',
    ATTRIBUTE_CODE_NOT_UNIQUE: 'attribute-code-not-unique',
    ATTRIBUTE_WITHOUT_OPTIONS: 'attribute-without-options',
    OPTION_NAME_REQUIRED: 'option-name-required',
    OPTION_NAME_NOT_UNIQUE: 'option-name-not-unique',
    OPTION_CODE_REQUIRED: 'option-code-required',
    OPTION_CODE_NOT_UNIQUE: 'option-code-not-unique',
    OPTION_NOT_FROM_PLATFORM: 'option-not-from-platform'
} as const;

type ComposerValidationErrorCode = (typeof ComposerValidationErrorCode)[keyof typeof ComposerValidationErrorCode];
type ValidatableNode = Pick<ComposerAttributeNode | ComposerAttributeOptionNode, 'brokenValues'>;

const normalizeComparableValue = (value: string) => value.trim().toLocaleLowerCase();
const isConnectedToStore = (node: { platformAttribute?: { id: string } }) => Boolean(node.platformAttribute?.id);

const countValues = <T>(items: T[], getValue: (item: T) => string) => {
    const counts = new Map<string, number>();
    items.forEach((item) => {
        const value = normalizeComparableValue(getValue(item));
        if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
    });
    return counts;
};

const addValidationError = (node: ValidatableNode, code: ComposerValidationErrorCode, message: string) => {
    node.brokenValues.set(code, message);
};

export const validateComposerOptions = (attribute: ComposerAttributeNode, optionList: ComposerAttributeOptionNode[]): ComposerAttributeOptionNode[] => {
    const options = optionList.map((option) => ({ ...option, brokenValues: new Map<string, string>() }));
    const nameCounts = countValues(options, (option) => option.name);
    const localOptions = options.filter((option) => !isConnectedToStore(option));
    const localCodeCounts = countValues(localOptions, (option) => option.code);

    options.forEach((option) => {
        // An option carrying its own price adjustment is keyed by that price, not by a code of its own.
        const hasCustomPricing = attribute.pricings.some((pricing) => pricing.selectedOptionGuid === option.id);
        if (!normalizeComparableValue(option.name)) {
            addValidationError(option, ComposerValidationErrorCode.OPTION_NAME_REQUIRED, 'This option needs a name.');
        }

        if ((nameCounts.get(normalizeComparableValue(option.name)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_NAME_NOT_UNIQUE,
                `Another option in “${attribute.name}” uses the name “${option.name.trim()}”. Option names must be unique within an attribute.`
            );
        }

        if (!isConnectedToStore(option) && !normalizeComparableValue(option.code) && !hasCustomPricing) {
            addValidationError(option, ComposerValidationErrorCode.OPTION_CODE_REQUIRED, `The local option “${option.name}” needs a code.`);
        }

        if (!isConnectedToStore(option) && (localCodeCounts.get(normalizeComparableValue(option.code)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_CODE_NOT_UNIQUE,
                `Another local option in “${attribute.name}” uses the code “${option.code.trim()}”. Codes must be unique for options not connected to the store.`
            );
        }

        if (isConnectedToStore(attribute) && !isConnectedToStore(option)) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_NOT_FROM_PLATFORM,
                `The option “${option.name}” is not connected to a store option, but its attribute “${attribute.name}” is connected to the store.`
            );
        }
    });

    return options;
};

export const upsertAndValidateComposerOption = (attribute: ComposerAttributeNode, option: ComposerAttributeOptionNode): ComposerAttributeNode => {
    const optionExists = attribute.options.some((candidate) => candidate.id === option.id);
    const nextOptions = optionExists ? attribute.options.map((candidate) => (candidate.id === option.id ? option : candidate)) : [...attribute.options, option];

    return {
        ...attribute,
        options: validateComposerOptions(attribute, nextOptions)
    };
};

export const validateComposerAttributes = (attributeList: ComposerAttributeNode[]): ComposerAttributeNode[] => {
    const validatedAttributes = attributeList.map((attribute) => ({
        ...attribute,
        brokenValues: new Map<string, string>(),
        options:
            attribute.type === ATTRIBUTE_NODE_TYPE
                ? validateComposerOptions(attribute, attribute.options)
                : attribute.options.map((option) => ({ ...option, brokenValues: new Map<string, string>() }))
    }));
    const attributes = validatedAttributes.filter((attribute) => attribute.type === ATTRIBUTE_NODE_TYPE);
    const nameCounts = countValues(attributes, (attribute) => attribute.name);
    const localAttributes = attributes.filter((attribute) => !isConnectedToStore(attribute));
    const localCodeCounts = countValues(localAttributes, (attribute) => attribute.code);

    attributes.forEach((attribute) => {
        if (!normalizeComparableValue(attribute.name)) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED, 'This attribute needs a name.');
        }

        if ((nameCounts.get(normalizeComparableValue(attribute.name)) ?? 0) > 1) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE,
                `Another attribute uses the name “${attribute.name.trim()}”. Attribute names must be unique.`
            );
        }

        if (!isConnectedToStore(attribute) && !normalizeComparableValue(attribute.code)) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED, `The local attribute “${attribute.name}” needs a code.`);
        }

        if (!isConnectedToStore(attribute) && (localCodeCounts.get(normalizeComparableValue(attribute.code)) ?? 0) > 1) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
                `Another local attribute uses the code “${attribute.code.trim()}”. Codes must be unique for attributes not connected to the store.`
            );
        }

        if (attribute.options.length === 0) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.ATTRIBUTE_WITHOUT_OPTIONS,
                `The attribute “${attribute.name}” must have at least one option.`
            );
        }
    });

    return validatedAttributes;
};

export const getComposerNodeValidationMessages = (node: ValidatableNode) => Array.from(new Set(node.brokenValues.values()));

export const getComposerAttributeValidationMessages = (attribute: ComposerAttributeNode) => {
    const messages = getComposerNodeValidationMessages(attribute);
    attribute.options.forEach((option) => {
        getComposerNodeValidationMessages(option).forEach((message) => messages.push(`Option “${option.name}”: ${message}`));
    });
    return Array.from(new Set(messages));
};

export const getComposerValidationMessages = (attributes: ComposerAttributeNode[]) =>
    attributes
        .filter((attribute) => attribute.type === ATTRIBUTE_NODE_TYPE)
        .flatMap((attribute) => getComposerAttributeValidationMessages(attribute).map((message) => `Attribute “${attribute.name}”: ${message}`));
